/** Enforced reading hook — blocks forward navigation briefly after an incorrect answer for explanation reading. */
import { useState, useEffect, useRef } from 'react';
import { useSession } from '../SessionContext';
import type { StepPhase } from '../types';

export const ENFORCED_READING_MS = 2000;

interface UseEnforcedReadingReturn {
  /** True while the user must read the explanation before proceeding */
  blockNext: boolean;
}

/**
 * After getting an answer wrong, blocks forward navigation for a few seconds
 * so the user reads the explanation.
 *
 * Only triggers on a fresh grading result (submitting/grading → graded transition),
 * not when reviewing a previously answered step.
 */
export function useEnforcedReading(): UseEnforcedReadingReturn {
  const { state } = useSession();
  const [blockNext, setBlockNext] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const prevPhaseRef = useRef<StepPhase>(state.phase);

  // Detect fresh incorrect grading result
  useEffect(() => {
    const wasSubmitting =
      prevPhaseRef.current === 'submitting' || prevPhaseRef.current === 'grading';
    prevPhaseRef.current = state.phase;

    if (wasSubmitting && state.phase === 'graded' && state.result && !state.result.isCorrect) {
      setBlockNext(true);
      timerRef.current = setTimeout(() => setBlockNext(false), ENFORCED_READING_MS);
    }
  }, [state.phase, state.result]);

  // Reset when navigating to a different step
  useEffect(() => {
    setBlockNext(false);
    clearTimeout(timerRef.current);
  }, [state.currentIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
    };
  }, []);

  return { blockNext };
}
